import { useEffect, useMemo, useState, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import type { AppShell } from "@/App"
import {
  listOrders,
  listOrderItems,
  listStockChecks,
  listSupplierOrders,
  listOrderEvents,
  listSupplierOrderItems,
  loadExpedition,
  loadWarehousePick,
  listEticdataDocNumbers,
  realEncNumber,
  applyBrandSuffix,
  resolvePaymentInfo,
  parseLineChanges,
  paymentLabel,
  getAppConfig,
  listAdjustments,
  listAppUsers,
  authorLabel,
  adjustmentStatusLabel,
  invokeAction,
  closeOrderManually,
  type OrderRow,
  type OrderItem,
  type StockCheck,
  type SupplierOrder,
  type SupplierOrderItem,
  type Expedition,
  type WarehousePick,
  type OrderItemAdjustment,
  isErpOrder,
  MANUAL_PAYMENT_METHODS,
  missingCustomerData,
  withExactQty,
} from "@/lib/api"
import { OrderComments } from "@/components/order-comments"
import { OrderChangeAlert } from "@/components/order-change-alert"
import { LineChangeBadge } from "@/components/line-change-badge"
import { SourceBadge } from "@/components/source-badge"
import { useOrderSources } from "@/lib/sources"
import type { TimelineEvent } from "@/components/timeline"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Timeline } from "@/components/timeline"
import { ApprovalCard } from "@/components/approval-card"
import { StepDots, STEP_DEFS, getStepFullLabel } from "@/components/steps"
import { Input } from "@/components/ui/input"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { ArrowLeft, Package, ScrollText, CheckCircle2, Truck, Download, ShoppingBag, Printer, XCircle, AlertTriangle, Clock } from "lucide-react"

interface OrderDetailProps {
  shell: AppShell | null
}

interface ArticleEditRow {
  sku: string
  name: string
  price: string
  brand: string
  reference: string
}

/** Previsões pré-definidas para a notificação de atraso. "Outro" → campo livre. */
const DELAY_PRESETS = ["3 dias", "1 semana", "2 semanas", "Outro"] as const

// Fallbacks do template (se a config ainda não carregou). Placeholders:
// {cliente} {encomenda} {previsao} {nota}.
const DELAY_SUBJECT_FALLBACK = "Atualização da sua encomenda {encomenda}"
const DELAY_BODY_FALLBACK = [
  "Olá {cliente},",
  "",
  "A tua encomenda {encomenda} vai demorar mais um pouco do que o previsto. Pedimos desculpa pelo incómodo.",
  "",
  "Nova previsão de envio: {previsao}",
  "",
  "{nota}",
  "",
  "Assim que a encomenda seguir, recebes o email com o número de seguimento.",
  "",
  "Obrigado pela compreensão.",
].join("\n")

function fillDelayTemplate(tpl: string, vars: Record<string, string>): string {
  return tpl.replace(/\{(cliente|encomenda|previsao|nota)\}/g, (_, k) => vars[k] ?? "")
}

export function OrderDetail({ shell }: OrderDetailProps) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [order, setOrder] = useState<OrderRow | null>(null)
  const [items, setItems] = useState<OrderItem[]>([])
  // Linhas do mesmo SKU numa só posição (quantidade somada) — igual à ENC, ao
  // PDF de separação e à Logística. `items` fica com as linhas reais (ajustes
  // referenciam o id de cada uma).
  const mergedItems = useMemo(() => {
    const bySku = new Map<string, OrderItem>()
    for (const it of items) {
      // SKU + descrição: o artigo genérico (9999) leva artigos diferentes.
      const key = `${it.sku.trim().toLowerCase()}|${String(it.name ?? "").trim().toLowerCase()}`
      const prev = bySku.get(key)
      if (prev) bySku.set(key, { ...prev, qty: Number(prev.qty) + Number(it.qty) })
      else bySku.set(key, { ...it, qty: Number(it.qty) })
    }
    return [...bySku.values()]
  }, [items])
  const [stockChecks, setStockChecks] = useState<StockCheck[]>([])
  const [suppliers, setSuppliers] = useState<SupplierOrder[]>([])
  const [supplierItems, setSupplierItems] = useState<SupplierOrderItem[]>([])
  const [events, setEvents] = useState<TimelineEvent[]>([])
  const [expedition, setExpedition] = useState<Expedition | null>(null)
  // Ajustes de armazém (informativo — não alteram ENC/fatura).
  const [adjustments, setAdjustments] = useState<OrderItemAdjustment[]>([])
  const [users, setUsers] = useState<Map<string, string>>(new Map())
  // Nº ENC atribuído pelo ETICDATA (quando o Hub o devolve).
  const [encNumber, setEncNumber] = useState<string | null>(null)
  // Envio do PDF de separação — null/sem email_sent_at = ainda NÃO enviado.
  const [pick, setPick] = useState<WarehousePick | null>(null)
  const sources = useOrderSources(shell)
  // Nº ENC escrito à mão (encomenda lançada diretamente no ETICDATA).
  const [encEditing, setEncEditing] = useState(false)
  const [encDraft, setEncDraft] = useState("")
  const [encSaving, setEncSaving] = useState(false)
  // Portes a orçamentar (ilhas): valor pago pelo cliente.
  const [quoteAmount, setQuoteAmount] = useState("")
  // "Pedir portes" envia email ao cliente → 1.º clique só arma a confirmação.
  const [quoteArmed, setQuoteArmed] = useState(false)
  // Cliente já avisado do levantamento → 1.º clique só arma o reenvio.
  const [resendArmed, setResendArmed] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [confirmingClose, setConfirmingClose] = useState(false)
  // "Criar artigos" edit popup — one editable row per unmatched SKU.
  const [articleRows, setArticleRows] = useState<ArticleEditRow[] | null>(null)
  // Notificar atraso — popup com a previsão a comunicar ao cliente.
  const [delayOpen, setDelayOpen] = useState(false)
  const [delayChoice, setDelayChoice] = useState<string>(DELAY_PRESETS[0])
  const [delayCustom, setDelayCustom] = useState("")
  const [delayNote, setDelayNote] = useState("")
  // Template do email de atraso (settings) — para o preview refletir o real.
  const [delayTpl, setDelayTpl] = useState<{ subject: string; body: string }>({
    subject: DELAY_SUBJECT_FALLBACK,
    body: DELAY_BODY_FALLBACK,
  })

  async function resendPickupNotice() {
    if (!shell || !order) return
    const armAndWarn = (at: string | null | undefined) => {
      setResendArmed(true)
      window.setTimeout(() => setResendArmed(false), 15000)
      shell.bridge.toast({
        variant: "info",
        description: `Cliente já avisado${at ? ` em ${new Date(at).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" })}` : ""}. Carrega outra vez para reenviar.`,
      })
    }
    if (order.pickup_notified_at && !resendArmed) return armAndWarn(order.pickup_notified_at)
    setBusy("notify_pickup_ready")
    try {
      const res = await invokeAction(shell, "notify_pickup_ready", {
        order_id: order.id,
        ...(resendArmed ? { force: true } : {}),
      })
      if (!res.success && res.error?.startsWith("JA_AVISADO|")) return armAndWarn(res.error.slice(11))
      setResendArmed(false)
      if (!res.success) throw new Error(res.error ?? "Falhou")
      shell.bridge.toast({ variant: "success", description: "Aviso enviado ao cliente." })
      await reload()
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : String(err) })
    } finally {
      setBusy(null)
    }
  }

  async function requestQuote() {
    if (!shell || !order) return
    const amount = Number(quoteAmount.replace(",", "."))
    if (!Number.isFinite(amount) || amount <= 0) {
      shell.bridge.toast({ variant: "error", description: "Indica o valor dos portes." })
      return
    }
    if (!quoteArmed) {
      setQuoteArmed(true)
      window.setTimeout(() => setQuoteArmed(false), 15000)
      shell.bridge.toast({
        variant: "info",
        description: `O Shopify vai enviar ao cliente um pedido de pagamento de ${amount.toFixed(2).replace(".", ",")} €. Carrega outra vez para confirmar.`,
      })
      return
    }
    setQuoteArmed(false)
    setBusy("request_shipping_quote")
    try {
      const res = await invokeAction(shell, "request_shipping_quote", {
        order_id: order.id,
        amount,
        ...(order.shipping_quote_requested_at ? { force: true } : {}),
      })
      if (!res.success) throw new Error(res.error ?? "Falhou")
      shell.bridge.toast({ variant: "success", description: "Pedido de portes enviado ao cliente pelo Shopify." })
      await reload()
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : String(err) })
    } finally {
      setBusy(null)
    }
  }

  async function saveEncNumber() {
    if (!shell || !id) return
    setEncSaving(true)
    try {
      const res = await invokeAction<{ document_number: string; advanced: boolean }>(shell, "set_enc_number", {
        order_id: id,
        document_number: encDraft,
      })
      if (!res.success) throw new Error(res.error ?? "Falhou")
      setEncNumber(res.data?.document_number ?? encDraft)
      setEncEditing(false)
      shell.bridge.toast({
        variant: "success",
        description: `Nº ENC registado${res.data?.advanced ? " — encomenda avança para separação" : ""}.`,
      })
      await reload()
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : String(err) })
    } finally {
      setEncSaving(false)
    }
  }

  const reload = useCallback(async () => {
    if (!shell || !id) return
    try {
      const [orders, itemsRes, stockRes, suppliersRes, supplierItemsRes, eventsRes, expeditionRes, adjRes, usersRes, encMapRes, pickRes] = await Promise.all([
        listOrders(shell, 100),
        listOrderItems(shell, id),
        listStockChecks(shell, id),
        listSupplierOrders(shell, id),
        listSupplierOrderItems(shell, id),
        listOrderEvents(shell, id),
        loadExpedition(shell, id),
        listAdjustments(shell, id),
        listAppUsers(shell),
        listEticdataDocNumbers(shell),
        loadWarehousePick(shell, id),
      ])
      setAdjustments(adjRes)
      setUsers(usersRes)
      setEncNumber(realEncNumber(encMapRes.get(id) ?? null))
      setPick(pickRes)
      const found = orders.find((o) => o.id === id) ?? null
      setOrder(found)
      // Ordenar por SKU ascendente — MESMO critério da ENC no ETICDATA
      // (email + PDF de separação usam a mesma ordem).
      setItems([...itemsRes].map(withExactQty).sort((a, b) => (a.sku < b.sku ? -1 : 1)))
      setStockChecks(stockRes)
      setSuppliers(suppliersRes)
      setSupplierItems(supplierItemsRes)
      setEvents(eventsRes)
      setExpedition(expeditionRes)
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : String(err) })
    }
  }, [shell, id])

  useEffect(() => {
    void reload()
    const t = setInterval(() => { void reload() }, 5000)
    return () => clearInterval(t)
  }, [reload])

  async function runAction(
    verb: Parameters<typeof invokeAction>[1],
    params: Record<string, unknown>,
    label: string,
    busyKey?: string,
  ) {
    if (!shell) return
    setBusy(busyKey ?? verb)
    try {
      const res = await invokeAction(shell, verb, params)
      if (res.success) {
        shell.bridge.toast({ variant: "success", description: `${label} concluído.` })
      } else {
        shell.bridge.toast({ variant: "error", description: res.error || `Falha em ${label}` })
      }
      await reload()
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : String(err) })
    } finally {
      setBusy(null)
    }
  }

  // Build the edit popup: one row per unmatched SKU, name pre-filled UPPERCASE
  // + sufixo da marca (mesma regra do engine).
  async function openArticleDialog() {
    // Mapa marca → sufixo (settings). Sem config, usa as 3 primeiras letras.
    let suffixMap: Record<string, unknown> = {}
    if (shell) {
      try {
        const cfg = await getAppConfig(shell)
        const m = cfg.config.brand_name_suffix_map ?? cfg.schema.brand_name_suffix_map?.default
        if (m && typeof m === "object") suffixMap = m as Record<string, unknown>
      } catch {
        /* sem config — o engine aplica o sufixo à mesma no envio */
      }
    }
    const latest = new Map<string, StockCheck>()
    for (const c of [...stockChecks].sort((a, b) => (a.checked_at < b.checked_at ? -1 : 1))) {
      latest.set(c.sku, c)
    }
    const itemBySku = new Map(items.map((i) => [i.sku, i]))
    const rows: ArticleEditRow[] = []
    for (const c of latest.values()) {
      if (c.matched) continue
      const it = itemBySku.get(c.sku)
      rows.push({
        sku: c.sku,
        // Nome curado (alt_name do catálogo) quando preenchido; senão o título
        // Shopify da encomenda. O operador pode ainda editar antes de enviar.
        name: applyBrandSuffix(
          (c.alt_name?.trim() || it?.name || c.sku).toUpperCase(),
          it?.vendor ?? null,
          suffixMap,
        ),
        // PREÇO DE CUSTO (não o de venda) — é o que vai para o ETICDATA.
        // Vem do CRM (cost_price) resolvido na validação de stock; se o CRM não
        // tem custo, fica vazio para o operador o escrever (nunca pré-enchemos
        // com o preço de venda, senão iria como custo).
        price: c.cost_price != null ? String(c.cost_price) : "",
        brand: it?.vendor || "—",
        // Referência ETICDATA composta (<marca>.<sku>) — editável. Vem do que
        // foi realmente pesquisado; fallback ao SKU cru.
        reference: c.eticdata_ref?.trim() || c.sku,
      })
    }
    setArticleRows(rows)
  }

  async function confirmCreateArticles() {
    if (!order || !articleRows) return
    const articles = articleRows.map((r) => ({
      sku: r.sku,
      name: r.name,
      price: r.price === "" ? undefined : Number(r.price),
      reference: r.reference.trim() || undefined,
    }))
    setArticleRows(null)
    await runAction("create_articles", { order_id: order.id, articles }, "Criação de artigos")
  }

  async function openDelayDialog() {
    setDelayOpen(true)
    if (!shell) return
    try {
      const cfg = await getAppConfig(shell)
      const subject = String(cfg.config.delay_email_subject ?? cfg.schema.delay_email_subject?.default ?? DELAY_SUBJECT_FALLBACK)
      const body = String(cfg.config.delay_email_body ?? cfg.schema.delay_email_body?.default ?? DELAY_BODY_FALLBACK)
      setDelayTpl({ subject, body })
    } catch {
      // sem config — o preview usa os fallbacks.
    }
  }

  async function sendDelayNotice() {
    if (!order) return
    const eta = (delayChoice === "Outro" ? delayCustom : delayChoice).trim()
    if (!eta) {
      shell?.bridge.toast({ variant: "error", description: "Indica a previsão." })
      return
    }
    setDelayOpen(false)
    await runAction(
      "notify_delay",
      { order_id: order.id, eta, note: delayNote.trim() || undefined },
      "Notificação de atraso",
    )
    setDelayNote("")
    setDelayCustom("")
  }

  async function closeManually() {
    if (!shell || !order) return
    // No window.confirm — sandboxed iframe blocks browser modals. Confirmation
    // is a two-click inline state (confirmingClose).
    setBusy("close_order")
    try {
      await closeOrderManually(shell, order.id)
      shell.bridge.toast({ variant: "success", description: "Encomenda concluída." })
      setConfirmingClose(false)
      await reload()
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : String(err) })
    } finally {
      setBusy(null)
    }
  }

  if (!shell) return <div className="p-6 text-sm text-muted-foreground">Standalone dev — sem dados.</div>
  if (!order) return <div className="p-6 text-sm text-muted-foreground">A carregar encomenda…</div>

  const states = order.step_states ?? {}
  // Última edição no Shopify: o que mudou em cada linha (novo / de → para / removido).
  const lineChanges = parseLineChanges(order)
  const removedLines = [...lineChanges.values()].filter((c) => c.after == null)
  const currentStep = order.current_step
  // Forma de pagamento + levantamento em loja (derivados do payload Shopify).
  const payInfo = resolvePaymentInfo(order)
  const sentAt = pick?.email_sent_at ?? null

  const draftSuppliers = suppliers.filter((s) => s.status === "draft")
  const itemsBySupplier = (supplierOrderId: string): SupplierOrderItem[] =>
    supplierItems.filter((it) => it.supplier_order_id === supplierOrderId)
  const latestStockBySku = new Map<string, StockCheck>()
  for (const check of [...stockChecks].sort((a, b) =>
    a.checked_at < b.checked_at ? -1 : 1,
  )) {
    latestStockBySku.set(check.sku, check)
  }
  const unmatchedChecks = Array.from(latestStockBySku.values()).filter((s) => !s.matched)
  const hasUnmatched = unmatchedChecks.length > 0
  // "Config gap": unmatched SKUs whose queried ref equals the raw SKU →
  // brand external_code wasn't applied (brand sem código, ou host sem
  // catalog deploy). Distinct from a genuine ETICDATA miss.
  const configGapChecks = unmatchedChecks.filter(
    (s) => !s.eticdata_ref || s.eticdata_ref === s.sku,
  )
  const isConfigGap = configGapChecks.length > 0

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center justify-between border-b px-6 py-4">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
            <ArrowLeft className="size-4" />
          </Button>
          <div className="min-w-0">
            <h1 className="truncate text-lg font-semibold leading-tight">
              {encNumber ?? order.shopify_order_number ?? order.id.slice(0, 8)} ·{" "}
              {order.customer_name ?? "—"}
            </h1>
            <p className="text-xs text-muted-foreground">
              {order.ship_city ?? "—"} {order.ship_zip ?? ""} · {Number(order.total_amount ?? 0).toFixed(2)} {order.currency ?? "EUR"}
              {order.customer_nif && ` · NIF ${order.customer_nif}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Fonte da encomenda — canto superior direito, em MAIÚSCULAS. */}
          <SourceBadge name={sources.sourceName(order)} section={sources.sectionOf(order)} />
          {order.completed_at ? (
            <Badge variant="success">Concluída</Badge>
          ) : (
            <Badge variant="warning">{getStepFullLabel(currentStep)}</Badge>
          )}
          {!order.completed_at && (
            <Button
              variant={order.delay_notified_at ? "outline" : "outline"}
              size="sm"
              disabled={busy === "notify_delay" || !order.customer_email}
              title={order.customer_email ? "Notificar o cliente de atraso" : "A encomenda não tem email de cliente"}
              onClick={() => void openDelayDialog()}
            >
              <Clock className="mr-1.5 size-3.5" />
              {busy === "notify_delay"
                ? "A enviar…"
                : order.delay_notified_at
                  ? "Atraso notificado"
                  : "Notificar atraso"}
            </Button>
          )}
          {!order.completed_at && (
            confirmingClose ? (
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground">Concluir mesmo?</span>
                <Button
                  variant="default"
                  size="sm"
                  disabled={busy === "close_order"}
                  onClick={closeManually}
                >
                  {busy === "close_order" ? "A fechar…" : "Confirmar"}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy === "close_order"}
                  onClick={() => setConfirmingClose(false)}
                >
                  Cancelar
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmingClose(true)}
              >
                <CheckCircle2 className="mr-1.5 size-3.5" />
                Concluir manualmente
              </Button>
            )
          )}
        </div>
      </header>

      <div className="grid flex-1 grid-cols-1 gap-4 overflow-auto p-6 lg:grid-cols-12">
        {/* Alterada/cancelada no Shopify + ENCs antigas por anular no ETICDATA. */}
        {(order.cancelled_at || order.change_alert || order.enc_update_pending) && (
          <div className="order-first lg:col-span-12">
            <OrderChangeAlert shell={shell} order={order} allowConfirm />
          </div>
        )}
        {missingCustomerData(order).length > 0 && !order.cancelled_at && (
          <div className="order-first rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-900 lg:col-span-12">
            <strong>Faltam dados do cliente:</strong> {missingCustomerData(order).join(", ")} — preencher na encomenda no
            ETICDATA (a app atualiza sozinha em poucos minutos).
            {missingCustomerData(order).some((m) => m === "morada" || m === "código postal") &&
              " Sem morada não é possível criar a expedição."}
          </div>
        )}
        {/* COCKPIT (direita): pipeline · avanço manual · ações · comentários.
            DOM-first mas visualmente à direita via `order` — assim o cockpit
            fica contíguo e a coluna principal também. */}
        <div className="order-2 space-y-4 lg:col-span-5">
          {/* Pipeline horizontal */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle>Pipeline</CardTitle>
            </CardHeader>
            <CardContent className="pt-2">
              <div className="flex flex-wrap items-center gap-2">
                {STEP_DEFS.map((step) => {
                  const status = states[step.id] ?? (currentStep === step.id ? "current" : "pending")
                  return (
                    <Badge
                      key={step.id}
                      variant={
                        status === "done"
                          ? "default"
                          : status === "current"
                            ? "info"
                            : status === "waiting"
                              ? "warning"
                              : status === "error"
                                ? "destructive"
                                : "outline"
                      }
                    >
                      {step.label}
                    </Badge>
                  )
                })}
              </div>
              <div className="mt-4">
                <StepDots states={states} currentStep={currentStep} />
              </div>
            </CardContent>
          </Card>

          {/* Barra de avanço manual — recuar/saltar o passo ATUAL. Única (não
              repetida em cada card), por isso funciona mesmo sem card de ação. */}
          {!order.completed_at && currentStep && (
            <Card>
              <CardContent className="flex flex-wrap items-center gap-2 py-3">
                <span className="mr-auto text-xs text-muted-foreground">
                  Passo atual: <span className="font-medium text-foreground">{getStepFullLabel(currentStep)}</span>
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy === `go_back_step:${currentStep}`}
                  onClick={() =>
                    runAction("go_back_step", { order_id: order.id, step: currentStep }, "Passo anterior", `go_back_step:${currentStep}`)
                  }
                >
                  {busy === `go_back_step:${currentStep}` ? "A voltar…" : "← Passo anterior"}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground"
                  disabled={busy === `skip_step:${currentStep}`}
                  onClick={() =>
                    runAction("skip_step", { order_id: order.id, step: currentStep }, "Saltar passo", `skip_step:${currentStep}`)
                  }
                >
                  {busy === `skip_step:${currentStep}` ? "A saltar…" : "Saltar passo →"}
                </Button>
              </CardContent>
            </Card>
          )}


          {/* Stock validation is automatic (triggered by the orders/paid
              webhook). Only surface a card when it failed (SKUs sem match) —
              the manual button becomes "Revalidar stock". */}
          {states.stock === "error" && (
            <ApprovalCard
              title={
                isConfigGap
                  ? "SKUs sem código de marca"
                  : "SKUs sem match no ETICDATA"
              }
              description={
                isConfigGap
                  ? "A referência ETICDATA é <código da marca>.<sku>. Estes SKUs foram enviados sem prefixo — preenche o 'Código externo' da marca em Produtos → Marcas e revalida."
                  : "Alguns SKUs não foram encontrados no ETICDATA. Criar produtos antes de revalidar."
              }
              primaryLabel="Revalidar stock"
              loading={busy === "validate_stock"}
              onPrimary={() => runAction("validate_stock", { order_id: order.id }, "Revalidação de stock")}
              details={
                <ul className="ml-4 list-disc space-y-1">
                  {unmatchedChecks.map((s) => {
                    const composed = s.eticdata_ref && s.eticdata_ref !== s.sku
                    const dbg = s.raw_response
                    return (
                      <li key={s.id}>
                        <span className="font-mono">{s.sku}</span>
                        {s.eticdata_ref && (
                          <span className="text-muted-foreground">
                            {" → "}
                            <span className={composed ? "font-mono" : "font-mono text-orange-600"}>
                              {s.eticdata_ref}
                            </span>
                            {!composed && " (sem prefixo de marca)"}
                          </span>
                        )}
                        {dbg && (
                          <span className="ml-1 text-[10px] text-muted-foreground">
                            · ZLINK: {dbg.hub_success === false
                              ? `erro (${dbg.hub_error ?? "?"})`
                              : `${dbg.returned_count ?? 0} resultado(s)`}
                            {dbg.returned_refs && dbg.returned_refs.length > 0 && (
                              <> [{dbg.returned_refs.join(", ")}]</>
                            )}
                          </span>
                        )}
                      </li>
                    )
                  })}
                </ul>
              }
            />
          )}

          {states.forn === "waiting" && draftSuppliers.map((sup) => {
            const supItems = itemsBySupplier(sup.id)
            const busyKey = `approve_supplier_order:${sup.id}`
            return (
              <ApprovalCard
                key={sup.id}
                title={`Aprovar encomenda a ${sup.supplier_name}`}
                description={
                  sup.supplier_code
                    ? `${supItems.length} SKU(s) · fornecedor ${sup.supplier_code}${sup.brand ? ` (${sup.brand})` : ""}. Envia a encomenda ao ETICDATA via ZLINK.`
                    : `${supItems.length} SKU(s) sem marca mapeada. Mapeia a marca em Settings → Fornecedores antes de aprovar.`
                }
                variant={sup.supplier_code ? "info" : "warning"}
                primaryLabel="Aprovar e enviar"
                loading={busy === busyKey}
                onPrimary={() =>
                  runAction(
                    "approve_supplier_order",
                    { order_id: order.id, supplier_order_id: sup.id },
                    `Aprovação ${sup.supplier_name}`,
                    busyKey,
                  )
                }
                details={
                  <ul className="ml-4 list-disc space-y-1">
                    {supItems.map((it) => (
                      <li key={it.id}>
                        <span className="font-mono">{it.sku}</span> · {it.qty} em falta
                      </li>
                    ))}
                  </ul>
                }
              />
            )
          })}

          {states.forn === "waiting" && (
            <div className="flex items-center justify-between rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground">
              <span>O fornecedor é tratado fora da app? Podes avançar sem encomendar.</span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy === "go_back_step:forn"}
                  onClick={() => runAction("go_back_step", { order_id: order.id, step: "forn" }, "Passo anterior", "go_back_step:forn")}
                >
                  {busy === "go_back_step:forn" ? "A voltar…" : "← Passo anterior"}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy === "skip_supplier_order"}
                  onClick={() => runAction("skip_supplier_order", { order_id: order.id }, "Saltar fornecedor")}
                >
                  {busy === "skip_supplier_order" ? "A saltar…" : "Saltar fornecedor →"}
                </Button>
              </div>
            </div>
          )}

          {states.eticdata === "current" && (
            <ApprovalCard
              title={isErpOrder(order) ? "A ENC já existe no ETICDATA" : "Criar encomenda cliente no ETICDATA"}
              description={
                isErpOrder(order)
                  ? "Esta encomenda veio do ETICDATA — não se cria outra. Segue para a separação."
                  : "Cria a ENC cliente com os dados do cliente Shopify. SKUs sem match no ETICDATA usam a referência fallback (default 9999) com a descrição e preço originais."
              }
              variant="info"
              primaryLabel={isErpOrder(order) ? "Seguir para a separação" : "Criar ENC cliente"}
              loading={busy === "create_eticdata_order"}
              onPrimary={() => runAction("create_eticdata_order", { order_id: order.id }, "Criação ENC cliente")}
            />
          )}

          {states.eticdata === "error" && (
            <ApprovalCard
              title="Falha a criar encomenda no ETICDATA"
              description="O ZLINK Hub rejeitou o payload. Verifica settings (NIF, doc_type, country_id) em /settings e tenta novamente."
              variant="warning"
              primaryLabel="Tentar novamente"
              loading={busy === "create_eticdata_order"}
              onPrimary={() => runAction("create_eticdata_order", { order_id: order.id }, "Criação ETICDATA")}
              details={
                <div className="text-xs text-muted-foreground">
                  Último erro: <span className="font-mono">
                    {events
                      .filter((e) => e.step === "eticdata" && e.tag === "etic" && /falha/i.test(e.title))
                      .slice(-1)[0]?.description ?? "—"}
                  </span>
                </div>
              }
            />
          )}

          {/* Separação suspensa (edição por pagar / portes / cancelada): sem ações de armazém. */}
          {states.logistica === "current" && (order.payment_pending_at || order.cancelled_at) && (
            <Card className="border-amber-400 bg-amber-50/60">
              <CardContent className="py-3 text-sm text-amber-900">
                <strong>Separação suspensa</strong> —{" "}
                {order.cancelled_at
                  ? "encomenda cancelada no Shopify."
                  : "editada no Shopify e a aguardar o pagamento da diferença. A lista nova segue para o armazém quando pagarem."}
              </CardContent>
            </Card>
          )}

          {states.logistica === "current" && !order.payment_pending_at && !order.cancelled_at && (
            <ApprovalCard
              // O texto reflecte o ESTADO REAL do envio: dizer "foi enviado"
              // quando ainda não foi deixava o armazém à espera de um email
              // que nunca chegou.
              title={sentAt ? "Confirmar separação de armazém" : "Enviar separação ao armazém"}
              description={
                sentAt
                  ? `Email enviado a ${pick?.email_to ?? "—"} em ${new Date(sentAt).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" })}. Confirma assim que a logística terminar para libertar o passo de fatura.`
                  : "O PDF de separação ainda NÃO foi enviado. Envia-o ao armazém para a separação começar."
              }
              variant="info"
              primaryLabel={sentAt ? "Marcar separação concluída" : "Enviar email de separação"}
              loading={busy === "mark_pick_done" || busy === "send_warehouse_pdf"}
              onPrimary={() =>
                sentAt
                  ? runAction("mark_pick_done", { order_id: order.id }, "Picking")
                  : runAction("send_warehouse_pdf", { order_id: order.id }, "Email de separação")
              }
              secondaryLabel={
                sentAt ? (busy === "send_warehouse_pdf" ? "A reenviar…" : "Reenviar email") : undefined
              }
              onSecondary={
                sentAt
                  ? () => runAction("send_warehouse_pdf", { order_id: order.id }, "Reenvio email armazém")
                  : undefined
              }
            />
          )}

          {/* Portes a orçamentar: nada segue para ENC/armazém sem os portes pagos. */}
          {order.shipping_quote_required_at && !order.shipping_quote_paid_at && !order.cancelled_at && (
            <Card className="border-amber-400 bg-amber-50/60">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Aguarda portes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p>
                  Envio a orçamentar{order.ship_zip ? ` (${order.ship_zip})` : ""}. O cliente pagou o material; envia-lhe o
                  valor dos portes e, quando pagar, regista aqui. A ENC e o armazém ficam bloqueados até lá.
                </p>
                {order.shipping_quote_requested_at && (
                  <p className="font-medium">
                    Portes pedidos pelo Shopify
                    {order.shipping_quote_amount != null
                      ? ` (${Number(order.shipping_quote_amount).toFixed(2).replace(".", ",")} €)`
                      : ""}{" "}
                    em {new Date(order.shipping_quote_requested_at).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" })} —
                    liberta sozinha quando o cliente pagar.
                  </p>
                )}
                <div className="flex items-center gap-2">
                  <Input
                    className="h-8 w-32"
                    inputMode="decimal"
                    placeholder="Portes (€)"
                    value={quoteAmount}
                    onChange={(e) => setQuoteAmount(e.target.value)}
                  />
                  <Button
                    size="sm"
                    disabled={busy === "shipping_quote_paid" || quoteAmount.trim() === ""}
                    onClick={() =>
                      runAction(
                        "shipping_quote_paid",
                        { order_id: order.id, amount: Number(quoteAmount.replace(",", ".")) },
                        "Portes pagos",
                      )
                    }
                  >
                    Portes pagos — libertar
                  </Button>
                  <Button
                    size="sm"
                    variant={quoteArmed ? "default" : "outline"}
                    disabled={busy === "request_shipping_quote" || quoteAmount.trim() === ""}
                    onClick={() => void requestQuote()}
                  >
                    {busy === "request_shipping_quote"
                      ? "A pedir…"
                      : quoteArmed
                        ? "Confirmar: pedir ao cliente"
                        : order.shipping_quote_requested_at
                          ? "Pedir outra vez pelo Shopify"
                          : "Pedir portes ao cliente (Shopify)"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Levantamento em loja: primeiro o cliente levanta, depois fatura. */}
          {states.fatura === "waiting" && payInfo.pickupInStore && !order.picked_up_at && (
            <ApprovalCard
              title="Aguarda levantamento do cliente"
              description={
                order.pickup_notified_at
                  ? `Cliente avisado por email em ${new Date(order.pickup_notified_at).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" })}. Marca quando levantar para faturar.`
                  : "O armazém ainda não marcou a encomenda como pronta para levantamento (é lá que o cliente é avisado). Podes avisar daqui."
              }
              variant="info"
              primaryLabel="Marcar como levantada"
              loading={busy === "mark_picked_up" || busy === "notify_pickup_ready"}
              onPrimary={() => runAction("mark_picked_up", { order_id: order.id }, "Levantada")}
              secondaryLabel={
                resendArmed
                  ? "Confirmar reenvio ao cliente"
                  : order.pickup_notified_at
                    ? "Reenviar aviso ao cliente"
                    : "Enviar aviso ao cliente"
              }
              onSecondary={() => void resendPickupNotice()}
            />
          )}

          {states.fatura === "waiting" && (!payInfo.pickupInStore || !!order.picked_up_at) && (
            <ApprovalCard
              title="Faturar no ETICDATA"
              description={
                payInfo.pickupInStore
                  ? `Levantada em ${new Date(order.picked_up_at as string).toLocaleString("pt-PT", { dateStyle: "short" })}. Emite a fatura no ETICDATA (série ${order.customer_nif ? "FAT-W" : "FA-SW"}) — ao marcar como faturado a encomenda fica concluída (sem expedição).`
                  : `Emite a fatura no ETICDATA (série ${order.customer_nif ? "FAT-W (com NIF)" : "FA-SW (simplificada)"}) e marca como faturado para libertar a expedição.`
              }
              variant="info"
              primaryLabel="Faturado"
              loading={busy === "issue_invoice"}
              onPrimary={() =>
                runAction(
                  "issue_invoice",
                  { order_id: order.id, series: order.customer_nif ? "FAT-W" : "FA-SW" },
                  "Faturado",
                )
              }
            />
          )}

          {states.expedicao === "waiting" && (
            <ApprovalCard
              title="Criar expedição Correos Expresso"
              description={`Destino: ${order.ship_city ?? "—"} ${order.ship_zip ?? ""} · ${Number(order.weight_kg ?? 1).toFixed(1)} kg`}
              primaryLabel="Criar expedição"
              loading={busy === "create_expedition"}
              onPrimary={() => runAction("create_expedition", { order_id: order.id }, "Expedição")}
            />
          )}

          {expedition?.tracking_number && (
            <Card>
              <CardHeader className="pb-2 flex-row items-center gap-2 space-y-0">
                <Truck className="size-4 text-muted-foreground" />
                <CardTitle>Expedição Correos Expresso</CardTitle>
                {expedition.status && (
                  <Badge variant="outline" className="ml-auto">{expedition.status}</Badge>
                )}
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                <div>
                  <span className="text-muted-foreground">Tracking:</span>{" "}
                  <span className="font-mono font-medium">{expedition.tracking_number}</span>
                </div>
                {expedition.pickup_number && (
                  <div className="text-muted-foreground">
                    Recolha {expedition.pickup_number}
                    {expedition.pickup_date ? ` · ${expedition.pickup_date}` : ""}
                    {expedition.pickup_cancelled_at && (
                      <span className="ml-1.5 font-medium text-destructive">· cancelada</span>
                    )}
                  </div>
                )}
                {expedition.shopify_fulfilled_at && (
                  <div className="flex items-center gap-1.5 text-emerald-700">
                    <CheckCircle2 className="size-3.5" />
                    Shopify atualizado · cliente notificado
                  </div>
                )}
                <div className="flex flex-wrap gap-2 pt-2">
                  {expedition.label_pdf_url ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(expedition.label_pdf_url!, "_blank", "noopener")}
                      >
                        <Download className="mr-1.5 size-3.5" />
                        Ver/Descarregar etiqueta
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busy === "print_label"}
                        onClick={() => runAction("print_label", { order_id: order.id }, "Impressão da etiqueta")}
                      >
                        <Printer className="mr-1.5 size-3.5" />
                        {busy === "print_label" ? "A imprimir…" : "Imprimir"}
                      </Button>
                    </>
                  ) : (
                    <span className="self-center text-xs text-muted-foreground">Etiqueta indisponível.</span>
                  )}
                  <Button
                    variant={expedition.shopify_fulfilled_at ? "outline" : "default"}
                    size="sm"
                    disabled={busy === "update_shopify_fulfillment"}
                    onClick={() => runAction("update_shopify_fulfillment", { order_id: order.id }, "Atualização Shopify")}
                  >
                    <ShoppingBag className="mr-1.5 size-3.5" />
                    {busy === "update_shopify_fulfillment"
                      ? "A enviar…"
                      : expedition.shopify_fulfilled_at
                        ? "Reenviar ao Shopify"
                        : "Atualizar Shopify"}
                  </Button>
                  {expedition.pickup_number && !expedition.pickup_cancelled_at && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive"
                      disabled={busy === "cancel_pickup"}
                      onClick={() => runAction("cancel_pickup", { order_id: order.id }, "Cancelamento da recolha")}
                    >
                      <XCircle className="mr-1.5 size-3.5" />
                      {busy === "cancel_pickup" ? "A cancelar…" : "Cancelar recolha"}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Ajustes de armazém — o que a logística detetou ao separar.
              Informativo: NÃO altera a ENC nem a fatura. Fica ACIMA dos
              comentários; para estes casos dá para encomendar a fornecedor. */}
          {adjustments.length > 0 && (
            <Card className="border-amber-300 bg-amber-50/30">
              <CardHeader className="pb-2 flex-row items-center gap-2 space-y-0">
                <AlertTriangle className="size-4 text-amber-600" />
                <CardTitle>Ajustes de armazém ({adjustments.length})</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Registado pela logística ao separar (danificado / em falta). Não altera a
                  encomenda nem a fatura.
                </p>
                <ul className="space-y-2">
                  {adjustments.map((a) => {
                    const it = items.find((i) => i.id === a.order_item_id)
                    return (
                      <li key={a.id} className="rounded-md border bg-background px-3 py-2 text-sm">
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          <span className="font-mono text-xs">{it?.sku ?? "—"}</span>
                          <span className="font-medium">{it?.name ?? ""}</span>
                          <span className="ml-auto inline-flex items-center rounded-md border border-amber-400 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                            {a.qty}× {adjustmentStatusLabel(a.status)}
                          </span>
                        </div>
                        {a.comment && <p className="mt-1 text-muted-foreground">{a.comment}</p>}
                        <div className="mt-1 text-[11px] text-muted-foreground">
                          {authorLabel(a.created_by_name, a.created_by, users)} ·{" "}
                          {new Date(a.created_at).toLocaleString("pt-PT", {
                            dateStyle: "short",
                            timeStyle: "short",
                          })}
                        </div>
                      </li>
                    )
                  })}
                </ul>
                <Button
                  variant="default"
                  size="sm"
                  className="w-full"
                  disabled={busy === "supplier_order_from_adjustments"}
                  onClick={() =>
                    runAction(
                      "supplier_order_from_adjustments",
                      { order_id: order.id },
                      "Encomenda a fornecedor",
                    )
                  }
                >
                  <Package className="mr-1.5 size-3.5" />
                  {busy === "supplier_order_from_adjustments"
                    ? "A criar…"
                    : "Encomendar a fornecedor"}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Comentários — mesmo fio do detalhe de logística. */}
          <OrderComments shell={shell} orderId={order.id} />
        </div>

        {/* COLUNA PRINCIPAL (esquerda): dados · linhas · ajustes · histórico */}
        <div className="order-1 space-y-4 lg:col-span-7">
          {/* Cabeçalho — cliente, entrega, faturação */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle>Dados da encomenda</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-x-6 gap-y-3 pt-2 text-sm sm:grid-cols-3">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Cliente</div>
                <div className="font-medium">{order.customer_name ?? "—"}</div>
                {order.customer_email && (
                  <div className="text-xs text-muted-foreground">{order.customer_email}</div>
                )}
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Faturação <span className="normal-case">(ETICDATA)</span>
                </div>
                <div className="font-medium">
                  {order.customer_nif ? `NIF ${order.customer_nif}` : "Sem NIF"}
                </div>
                {(order.bill_name || order.bill_address) && (
                  <div className="text-xs text-muted-foreground">
                    {order.bill_name ?? order.customer_name ?? "—"}
                    {order.bill_address ? ` · ${order.bill_address}` : ""}
                    {order.bill_zip || order.bill_city
                      ? ` · ${order.bill_zip ?? ""} ${order.bill_city ?? ""}`.trim()
                      : ""}
                  </div>
                )}
                <div className="text-[11px] text-muted-foreground">
                  Série recomendada: {order.customer_nif ? "FAT-W" : "FA-SW"}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Entrega <span className="normal-case">(Correos)</span>
                </div>
                <div className="font-medium">
                  {order.ship_name ?? order.customer_name ?? "—"}
                </div>
                <div className="text-xs text-muted-foreground">
                  {order.ship_address ? `${order.ship_address} · ` : ""}
                  {order.ship_zip ?? ""} {order.ship_city ?? ""} · {order.ship_country ?? "PT"}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {Number(order.weight_kg ?? 1).toFixed(1)} kg
                  {order.ship_phone ? ` · ${order.ship_phone}` : ""}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Total</div>
                <div className="font-mono tabular-nums font-medium">
                  {Number(order.total_amount ?? 0).toFixed(2)} {order.currency ?? "EUR"}
                </div>
                <div className="text-xs text-muted-foreground">{order.items_count ?? 0} linhas</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Enc. ETICDATA
                </div>
                {encEditing ? (
                  <div className="mt-0.5 flex items-center gap-1.5">
                    <Input
                      autoFocus
                      className="h-7 w-44 font-mono text-xs"
                      placeholder="112426/620"
                      value={encDraft}
                      onChange={(e) => setEncDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") void saveEncNumber()
                        if (e.key === "Escape") setEncEditing(false)
                      }}
                      disabled={encSaving}
                    />
                    <Button size="sm" className="h-7" disabled={encSaving || !encDraft.trim()} onClick={() => void saveEncNumber()}>
                      {encSaving ? "…" : "Guardar"}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7" disabled={encSaving} onClick={() => setEncEditing(false)}>
                      Cancelar
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-sm font-medium">{encNumber ?? "—"}</span>
                    {/* Lançada à mão no ETICDATA → o operador regista o nº para o relatório diário. */}
                    <button
                      type="button"
                      className="text-[11px] text-sky-700 underline-offset-2 hover:underline"
                      onClick={() => {
                        setEncDraft(encNumber ?? "")
                        setEncEditing(true)
                      }}
                    >
                      {encNumber ? "corrigir" : "registar nº"}
                    </button>
                  </div>
                )}
                <div className="text-xs text-muted-foreground">
                  {isErpOrder(order) ? "ETICDATA" : "Online"}: {order.shopify_order_number ?? order.shopify_order_id} ·{" "}
                  {new Date(order.created_at).toLocaleString("pt-PT", { dateStyle: "short" })}
                </div>
                {order.erp_origin_ref && (
                  <div className="text-xs font-medium">Referente orçamento {order.erp_origin_ref}</div>
                )}
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Forma de pagamento
                </div>
                {isErpOrder(order) ? (
                  // O ERP não guarda o pagamento — indicação opcional, para rastreabilidade.
                  <select
                    className="mt-0.5 h-8 rounded-md border bg-background px-2 text-sm"
                    value={order.payment_method_manual ?? ""}
                    disabled={busy === "set_payment_method"}
                    onChange={(e) =>
                      void runAction("set_payment_method", { order_id: order.id, method: e.target.value || null }, "Forma de pagamento")
                    }
                  >
                    <option value="">Pago (não indicado)</option>
                    {MANUAL_PAYMENT_METHODS.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="font-medium">{paymentLabel(payInfo)}</div>
                )}
                {payInfo.pickupInStore && (
                  <div className="mt-0.5 inline-flex items-center rounded-md border border-amber-400 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                    Levantamento em loja
                  </div>
                )}
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Estado</div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {order.completed_at ? (
                    <Badge variant="success">Concluída</Badge>
                  ) : (
                    <Badge variant="warning">{getStepFullLabel(currentStep)}</Badge>
                  )}
                  {order.delay_notified_at && (
                    <Badge variant="destructive">Atraso · {order.delay_eta ?? "—"}</Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Items */}
          <Card>
            <CardHeader className="pb-2 flex-row items-center gap-2 space-y-0">
              <Package className="size-4 text-muted-foreground" />
              <CardTitle>Linhas ({mergedItems.length})</CardTitle>
              <div className="ml-auto flex items-center gap-2">
                {hasUnmatched && (
                  <Button
                    variant="default"
                    size="sm"
                    disabled={busy === "create_articles" || busy === "validate_stock"}
                    onClick={() => void openArticleDialog()}
                  >
                    {busy === "create_articles" ? "A criar…" : "Criar artigos"}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy === "validate_stock" || busy === "create_articles"}
                  onClick={() => runAction("validate_stock", { order_id: order.id }, "Revalidação de stock")}
                >
                  {busy === "validate_stock" ? "A validar…" : "Revalidar stock"}
                </Button>
              </div>
            </CardHeader>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>SKU</TableHead>
                  <TableHead>Produto</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Preço</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Stock</TableHead>
                  <TableHead>Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mergedItems.map((it) => {
                  const unitPrice = it.unit_price != null ? Number(it.unit_price) : null
                  const lineTotal = unitPrice != null ? unitPrice * it.qty : null
                  const check = latestStockBySku.get(it.sku)
                  const stockLabel =
                    check == null
                      ? "—"
                      : check.matched
                        ? String(check.available_qty ?? 0)
                        : "—"
                  let badge: { text: string; cls: string }
                  if (!check) {
                    badge = {
                      text: "Não validado",
                      cls: "bg-muted text-muted-foreground border-border",
                    }
                  } else if (!check.matched) {
                    badge = {
                      text: "SKU sem match",
                      cls: "border-destructive bg-destructive/10 text-destructive",
                    }
                  } else if (check.missing_qty > 0) {
                    badge = {
                      text: `Encomendar ${check.missing_qty}`,
                      cls: "border-amber-400 bg-amber-50 text-amber-800",
                    }
                  } else {
                    badge = {
                      text: "Em stock",
                      cls: "border-emerald-500 bg-emerald-50 text-emerald-800",
                    }
                  }
                  const composedRef =
                    check?.eticdata_ref && check.eticdata_ref !== it.sku
                      ? check.eticdata_ref
                      : null
                  const thumb = check?.image_url ?? null
                  return (
                    <TableRow key={it.id}>
                      <TableCell className="font-mono text-xs align-top">
                        <div>{it.sku}</div>
                        {composedRef && (
                          <div className="text-[10px] text-muted-foreground">{composedRef}</div>
                        )}
                        {thumb && (
                          <img
                            src={thumb}
                            alt={it.sku}
                            loading="lazy"
                            className="mt-1.5 size-12 rounded border object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = "none"
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <div>{it.name ?? "—"}</div>
                        <LineChangeBadge change={lineChanges.get(it.sku.trim().toLowerCase())} />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{it.qty}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {unitPrice != null ? unitPrice.toFixed(2) : "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-medium">
                        {lineTotal != null ? lineTotal.toFixed(2) : "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{stockLabel}</TableCell>
                      <TableCell>
                        <span
                          className={
                            "inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium " +
                            badge.cls
                          }
                        >
                          {badge.text}
                        </span>
                      </TableCell>
                    </TableRow>
                  )
                })}
                {removedLines.map((c) => (
                  <TableRow key={`removed-${c.sku}`} className="bg-amber-50/40">
                    <TableCell className="font-mono text-xs text-muted-foreground line-through">{c.sku}</TableCell>
                    <TableCell colSpan={6}>
                      <LineChangeBadge change={c} />
                    </TableCell>
                  </TableRow>
                ))}
                {mergedItems.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-xs text-muted-foreground">
                      Sem linhas.
                    </TableCell>
                  </TableRow>
                )}
                {Number(order.shipping_amount) > 0 && (
                  <TableRow className="bg-muted/30">
                    <TableCell className="font-mono text-xs">9191.TORP</TableCell>
                    <TableCell>Portes</TableCell>
                    <TableCell className="text-right tabular-nums">1</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {Number(order.shipping_amount).toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      {Number(order.shipping_amount).toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">—</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center rounded-md border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                        Portes
                      </span>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>

          {/* Histórico — passa para a coluna principal, sob as linhas. */}
          <Card>
            <CardHeader className="flex-row items-center gap-2 space-y-0">
              <ScrollText className="size-4 text-muted-foreground" />
              <CardTitle>Histórico</CardTitle>
            </CardHeader>
            <CardContent>
              <Timeline events={events} />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Notificar atraso — escolher a previsão a comunicar ao cliente */}
      <Dialog open={delayOpen} onOpenChange={setDelayOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Notificar atraso</DialogTitle>
            <DialogDescription>
              Para: {order.customer_email ?? "—"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className="mb-1.5 block text-[11px] text-muted-foreground">Previsão</label>
              <div className="flex flex-wrap gap-2">
                {DELAY_PRESETS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setDelayChoice(p)}
                    className={
                      "rounded-md border px-3 py-1.5 text-sm font-medium transition-colors " +
                      (delayChoice === p
                        ? "border-primary bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground")
                    }
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {delayChoice === "Outro" && (
              <div>
                <label className="mb-1 block text-[11px] text-muted-foreground">
                  Previsão (texto livre — vai tal e qual no email)
                </label>
                <Input
                  autoFocus
                  placeholder="ex: início de agosto"
                  value={delayCustom}
                  onChange={(e) => setDelayCustom(e.target.value)}
                />
              </div>
            )}

            <div>
              <label className="mb-1 block text-[11px] text-muted-foreground">
                Nota adicional (opcional)
              </label>
              <textarea
                rows={2}
                value={delayNote}
                onChange={(e) => setDelayNote(e.target.value)}
                placeholder="ex: o fornecedor teve rutura no artigo X."
                className="w-full resize-y rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            {/* Preview do email que o cliente vai receber. */}
            <div>
              <label className="mb-1 block text-[11px] text-muted-foreground">
                Pré-visualização do email
              </label>
              <div className="rounded-md border bg-muted/30 px-4 py-3 text-sm leading-relaxed">
                {(() => {
                  const vars = {
                    cliente: order.customer_name?.trim() || "Cliente",
                    encomenda: order.shopify_order_number
                      ? `#${String(order.shopify_order_number).replace(/^#/, "")}`
                      : String(order.shopify_order_id),
                    previsao: (delayChoice === "Outro" ? delayCustom : delayChoice).trim() || "…",
                    nota: delayNote.trim(),
                  }
                  const subject = fillDelayTemplate(delayTpl.subject, vars).replace(/\s+/g, " ").trim()
                  const body = fillDelayTemplate(delayTpl.body, vars)
                    .replace(/\n{3,}/g, "\n\n")
                    .trim()
                  return (
                    <>
                      <p className="font-medium">Assunto: {subject}</p>
                      <hr className="my-2 border-border" />
                      {body.split(/\n{2,}/).map((para, i) => (
                        <p key={i} className={i > 0 ? "mt-1.5 whitespace-pre-line" : "whitespace-pre-line"}>
                          {para}
                        </p>
                      ))}
                    </>
                  )
                })()}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDelayOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={() => void sendDelayNotice()}>
              <Clock className="mr-1.5 size-3.5" />
              Notificar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Criar artigos — editar nome (MAIÚSCULAS) + preço antes de enviar */}
      <Dialog open={articleRows != null} onOpenChange={(o) => !o && setArticleRows(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Criar artigos no ETICDATA</DialogTitle>
            <DialogDescription>
              Confirma o nome (enviado em MAIÚSCULAS) e o preço de cada artigo. A marca já está
              resolvida pela referência.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] space-y-3 overflow-auto py-1">
            {(articleRows ?? []).map((row, idx) => (
              <div key={row.sku} className="rounded-md border p-3">
                <div className="mb-2 flex items-center justify-between text-xs">
                  <span className="font-mono">{row.sku}</span>
                  <span className="text-muted-foreground">Marca: {row.brand}</span>
                </div>
                <div className="mb-2">
                  <label className="mb-1 block text-[11px] text-muted-foreground">
                    Referência ETICDATA <span className="normal-case">(&lt;marca&gt;.&lt;ref&gt;)</span>
                  </label>
                  <Input
                    className="font-mono"
                    value={row.reference}
                    onChange={(e) =>
                      setArticleRows((prev) =>
                        prev!.map((r, i) => (i === idx ? { ...r, reference: e.target.value } : r)),
                      )
                    }
                  />
                </div>
                <div className="grid grid-cols-[1fr_120px] gap-2">
                  <div>
                    <label className="mb-1 block text-[11px] text-muted-foreground">Nome</label>
                    <Input
                      value={row.name}
                      onChange={(e) =>
                        setArticleRows((prev) =>
                          prev!.map((r, i) => (i === idx ? { ...r, name: e.target.value.toUpperCase() } : r)),
                        )
                      }
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] text-muted-foreground">Preço de custo (€)</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={row.price}
                      onChange={(e) =>
                        setArticleRows((prev) =>
                          prev!.map((r, i) => (i === idx ? { ...r, price: e.target.value } : r)),
                        )
                      }
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArticleRows(null)}>
              Cancelar
            </Button>
            <Button onClick={() => void confirmCreateArticles()}>
              Criar {(articleRows ?? []).length} artigo{(articleRows ?? []).length === 1 ? "" : "s"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
